import { useState } from "react";
import { UserGroupIcon, PlusIcon } from "@heroicons/react/24/outline";
import Modal from "../../common/Modal";
import AddVotersToPrecinctModal from "./AddVotersToPrecinctModal";
import toast from "react-hot-toast";
import { validatePrecinct } from "../../../validation/voters/precinct";

function AddNewPrecinct({ isOpen, onClose, onSubmit }) {
  const [precinctName, setPrecinctName] = useState("");
  const [location, setLocation] = useState("");
  const [voters, setVoters] = useState([]);
  const [isAddVotersOpen, setIsAddVotersOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = () => {
    const data = {
      name: precinctName || "",
      location: location || "",
      voters: voters || [],
    };
    validatePrecinct(data).then((res) => {
      if (!res.valid) {
        setErrors(res.errors || {});
        toast.error("Please fix the highlighted errors");
        return;
      }
      setErrors({});
      onSubmit(data);
      toast.success("Precinct added successfully");
      onClose();
    });
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 text-sm font-medium"
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
      >
        Add Precinct
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Precinct"
      size="4xl"
      footer={footer}
    >
      {/* Form Fields */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Precinct Name
          </label>
          <input
            type="text"
            placeholder="Enter precinct name"
            value={precinctName}
            onChange={(e) => {
              setPrecinctName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location
          </label>
          <input
            type="text"
            placeholder="Enter location"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              if (errors.location) setErrors((prev) => ({ ...prev, location: undefined }));
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {errors.location && (
            <p className="mt-1 text-sm text-red-600">{errors.location}</p>
          )}
        </div>
      </div>

      {/* Voter Assignment */}
      <div className="border rounded-xl bg-gray-50 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-700 text-sm">
            Voter Assignment
          </h4>
          <button
            type="button"
            className="text-blue-600 text-sm font-medium flex items-center gap-1"
            onClick={() => setIsAddVotersOpen(true)}
          >
            <PlusIcon className="w-4 h-4" /> Assign Voter
          </button>
        </div>

        {voters.length === 0 ? (
          <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-10">
            <UserGroupIcon className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-gray-500 text-sm">No voters added yet</p>
          </div>
        ) : (
          <div className="border rounded-lg bg-white overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left">Voter</th>
                  <th className="px-4 py-3 text-left">Contact</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {voters.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{v.full_name || v.name}</div>
                      {v.voter_id && (
                        <div className="text-sm text-gray-500">
                          <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 mr-2">
                            {v.voter_id}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{v.contact_number || v.phone || v.mobile || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add voters modal */}
      <AddVotersToPrecinctModal
        isOpen={isAddVotersOpen}
        onClose={() => setIsAddVotersOpen(false)}
        onAssign={(arr) => {
          setVoters((prev) => {
            const map = new Map((prev || []).filter(Boolean).map((i) => [i.id, i]));
            (arr || []).forEach((i) => {
              if (i?.id) map.set(i.id, i);
            });
            return Array.from(map.values());
          });
        }}
      />
    </Modal>
  );
}

export default AddNewPrecinct;
